import { PhoneMockup, MockScreenHome } from "@/components/site/PhoneMockup";
import { HOME } from "@/constants/testIds";
import {
  Play,
  MapPin,
  Briefcase,
  Store,
  ShoppingBag,
  HeartPulse,
  Wrench,
  Droplets,
  Handshake,
  Siren
} from "lucide-react";

const chips = [
  { icon: MapPin, label: "Local Needs" },
  { icon: Briefcase, label: "Jobs" },
  { icon: Store, label: "Shops" },
  { icon: ShoppingBag, label: "Marketplace" },
  { icon: HeartPulse, label: "Blood Requests" },
  { icon: Siren, label: "SOS Safety" },
];

export default function Hero() {
  return (
    <section
      id="home"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-gradient-to-b from-[#FFF9F5] to-[#FDFBF7]"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-20 -left-20 w-96 h-96 rounded-full bg-orange-200/40 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-16 w-96 h-96 rounded-full bg-amber-100/60 blur-3xl" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-1.5 text-xs font-semibold text-[#4A4846] shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              A DIGISWING product · Made for Indian cities
            </div>

            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-[#1A1918] leading-[1.02]">
              Everything Local,{" "}
              <span className="brand-gradient-text">One App.</span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-[#4A4846] max-w-xl leading-relaxed">
              Discover local needs, find jobs, grow your business, buy or sell items, and
              stay connected with your community—all from one app.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#download"
                data-testid={HOME.heroDownloadAndroid}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 font-semibold shadow-lg shadow-emerald-500/25 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3.6 20.5 14 12 3.6 3.5c-.3.2-.6.7-.6 1.3v14.4c0 .6.3 1.1.6 1.3zm11.4-7.9 2.9 1.7-2.6 1.5-2-1.6 1.7-1.6zM4.6 21.2l10.8-6.3 2 1.6-11.5 6.6c-.4.2-.9.1-1.3-.1l0-.1v-1.7zM4.6 2.8l10.8 6.3-2 1.6L5.9 4.4c-.5-.3-.9-.4-1.3-.1v-.1c0-.5.3-.9.9-1.4h.1z" />
                </svg>
                Download for Android
              </a>
              <button
                type="button"
                data-testid={HOME.heroDownloadIphone}
                disabled
                aria-disabled="true"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#78716C] px-6 py-3.5 font-semibold border-2 border-[#EBE8E3] cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16.4 13.1c0-2.6 2.1-3.9 2.2-4-1.2-1.7-3-2-3.7-2-1.6-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.1 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8 1.5 0 2 .8 3.3.8 1.4 0 2.3-1.2 3.2-2.4 1-1.4 1.4-2.7 1.4-2.8-.1 0-2.8-1-2.8-4zM14.1 5.3c.7-.9 1.2-2.1 1-3.3-1 0-2.3.7-3 1.5-.7.8-1.3 2-1.1 3.1 1.1.1 2.3-.5 3.1-1.3z" />
                </svg>
                iPhone — Coming Soon
              </button>
              <a
                href="#screenshots"
                data-testid={HOME.heroWatchDemo}
                className="inline-flex items-center gap-2 rounded-full bg-transparent text-[#1A1918] hover:bg-white px-5 py-3.5 font-semibold border border-[#EBE8E3] transition-colors"
              >
                <Play className="w-4 h-4 fill-current" />
                Watch Demo
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-2.5">
              {chips.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EBE8E3] text-[#4A4846] px-3.5 py-1.5 text-xs font-semibold shadow-sm"
                >
                  <Icon className="w-3.5 h-3.5 text-[#FF5A00]" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center animate-fade-up">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-72 h-72 rounded-full bg-orange-300/25 blur-2xl" />
            </div>
            <div className="animate-float">
              <PhoneMockup>
                <MockScreenHome />
              </PhoneMockup>
            </div>

            {/* Floating badges */}
            <div className="hidden md:block absolute top-6 -left-2 bg-white rounded-2xl shadow-xl border border-[#EBE8E3] px-4 py-3 animate-float-delayed">
              <div className="flex items-center gap-2">
                <Handshake className="w-4 h-4 text-emerald-600" />
                <div>
                  <div className="text-[10px] text-[#78716C]">New Task Posted</div>
                  <div className="text-xs font-bold">&ldquo;Need Help Decorating Home...&rdquo;</div>
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute bottom-6 -right-4 bg-white rounded-2xl shadow-xl border border-[#EBE8E3] px-4 py-3 animate-float">
              <div className="flex items-center gap-2">
                <Droplets className="w-4 h-4 text-[#FF5A00]" />
                <div>
                  <div className="text-[10px] text-[#78716C]">Urgent Blood Request</div>
                  <div className="text-xs font-bold">AB+ Blood Required at Government Hospital...</div>
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute bottom-24 -left-6 bg-white rounded-2xl shadow-xl border border-[#EBE8E3] px-4 py-3 animate-float-delayed">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#FF5A00]" />
                <div>
                  <div className="text-[10px] text-[#78716C]">Service Request</div>
                  <div className="text-xs font-bold">Water pipe leakage needs immediate repair...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
